import React, { useState, useEffect, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TollIcon from "@mui/icons-material/Toll";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A" },
    error: { main: "#DC2626" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;
const coinSpin = keyframes`
  0%   { transform: rotateY(0deg); }
  50%  { transform: rotateY(180deg); }
  100% { transform: rotateY(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(196,146,42,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 860, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #C4922A 0%, #92680C 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(196,146,42,0.32)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #B8841F 0%, #7A5609 100%)",
    boxShadow: "0 6px 20px rgba(196,146,42,0.42)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#C4922A",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(196,146,42,0.1)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const CoinCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay" && p !== "isediting",
})(({ theme, itemdelay, isediting }) => ({
  borderRadius: 14,
  border: `1.5px solid ${
    isediting ? alpha("#C4922A", 0.35) : alpha("#000", 0.07)
  }`,
  boxShadow: isediting
    ? `0 0 0 3px ${alpha("#C4922A", 0.08)}, 0 4px 14px rgba(196,146,42,0.1)`
    : "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "18px 20px",
  display: "flex",
  alignItems: "center",
  gap: 16,
  marginBottom: 12,
  animation: `${cardEntrance} 0.4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  background: isediting ? alpha("#C4922A", 0.02) : "#fff",
  "&:last-child": { marginBottom: 0 },
  "&:hover": {
    boxShadow: isediting
      ? `0 0 0 3px ${alpha("#C4922A", 0.1)}, 0 6px 18px rgba(196,146,42,0.12)`
      : "0 4px 16px rgba(0,0,0,0.07), 0 8px 24px rgba(0,0,0,0.05)",
    transform: "translateY(-1px)",
    borderColor: isediting ? alpha("#C4922A", 0.45) : alpha("#C4922A", 0.2),
  },
}));

const CoinIconBox = styled(Box)(() => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  flexShrink: 0,
  background: "linear-gradient(135deg, #C4922A 0%, #F5C842 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 10px rgba(196,146,42,0.3)",
  transition: "transform 0.3s ease",
  "&:hover": { animation: `${coinSpin} 0.6s ease` },
}));

const StatBox = styled(Paper, {
  shouldForwardProp: (p) => p !== "accentcolor" && p !== "delay",
})(({ accentcolor, delay }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(accentcolor || "#C4922A", 0.12)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  background: "#fff",
  flex: 1,
  minWidth: 140,
}));

const StatIconBox = styled(Box)(({ accentcolor }) => ({
  width: 42,
  height: 42,
  borderRadius: 11,
  flexShrink: 0,
  background: alpha(accentcolor || "#C4922A", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora', sans-serif",
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const SkeletonCard = styled(Box)(() => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "18px 20px",
  display: "flex",
  alignItems: "center",
  gap: 16,
  background: "#fff",
  marginBottom: 12,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

// ─── CoinItem (memoized) ──────────────────────────────────────────────────────
const CoinItem = memo(
  ({
    item,
    index,
    isEditing,
    setCoinsValue,
    setCurrency,
    setAmount,
    setEditingCoin,
    handleDeleteCoin,
  }) => (
    <CoinCard itemdelay={index * 55} isediting={isEditing ? 1 : 0}>
      <CoinIconBox>
        <TollIcon sx={{ fontSize: 24, color: "#fff" }} />
      </CoinIconBox>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.8,
          }}
        >
          <Chip
            label={`${item.coins} Coins`}
            size="small"
            icon={<TollIcon sx={{ fontSize: "11px !important" }} />}
            sx={{
              fontFamily: "'Sora', sans-serif",
              fontWeight: 800,
              fontSize: 11,
              background: "linear-gradient(135deg, #C4922A, #F5C842)",
              color: "#fff",
              border: "none",
              "& .MuiChip-icon": { color: "#fff" },
              boxShadow: "0 2px 6px rgba(196,146,42,0.3)",
            }}
          />
          {isEditing && (
            <Chip
              label="Editing"
              size="small"
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 10,
                background: alpha("#C4922A", 0.1),
                color: "#C4922A",
                border: `1px solid ${alpha("#C4922A", 0.25)}`,
              }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 0.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AttachMoneyIcon sx={{ fontSize: 13, color: "text.disabled" }} />
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 600,
              }}
            >
              {item.currency}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <MonetizationOnIcon sx={{ fontSize: 13, color: "#059669" }} />
            <Typography
              sx={{
                fontSize: 12,
                color: "#059669",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
              }}
            >
              {Number(item.amount).toLocaleString()}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0 }}>
        <Tooltip title="Edit coin" arrow>
          <ActionBtn
            btncolor="edit"
            onClick={() => {
              setCoinsValue(item.coins);
              setCurrency(item.currency);
              setAmount(item.amount);
              setEditingCoin(item);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
            Edit
          </ActionBtn>
        </Tooltip>
        <Tooltip title="Delete coin" arrow>
          <ActionBtn
            btncolor="delete"
            onClick={() => handleDeleteCoin(item.id)}
          >
            <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
            Delete
          </ActionBtn>
        </Tooltip>
      </Stack>
    </CoinCard>
  )
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminGoldPage = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();

  const [coins, setCoins] = useState([]);
  const [coinsValue, setCoinsValue] = useState("");
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [editingCoin, setEditingCoin] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchCoins = useCallback(async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API_URL}/admin/coins/${countryId}/`);
      setCoins(res.data);
    } catch (err) {
      console.error("Error fetching coins:", err);
    } finally {
      setFetching(false);
    }
  }, [countryId]);

  const handleDeleteCoin = useCallback(
    async (id) => {
      try {
        await axios.delete(`/admin/coin/${id}/`);
        fetchCoins();
      } catch (err) {
        console.error("Error deleting coin:", err);
      }
    },
    [fetchCoins]
  );

  useEffect(() => {
    fetchCoins();
  }, [countryId, fetchCoins]);

  const handleCreateCoin = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/coins/${countryId}/`, {
        coins: coinsValue,
        currency,
        amount,
      });
      fetchCoins();
      resetForm();
    } catch (err) {
      console.error("Error creating coin:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCoin = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/admin/coin/${editingCoin.id}/`, {
        coins: coinsValue,
        currency,
        amount,
      });
      fetchCoins();
      resetForm();
    } catch (err) {
      console.error("Error updating coin:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCoinsValue("");
    setCurrency("");
    setAmount("");
    setEditingCoin(null);
  };

  // Stats
  const totalCoins = coins.reduce((s, c) => s + Number(c.coins || 0), 0);
  const totalAmount = coins.reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 28 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editingCoin ? "Edit Coin Pack" : "Coins & Rewards"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Country ID:{" "}
                <strong style={{ color: "#4B5563" }}>{countryId}</strong>
              </Typography>
            </Box>
            <Chip
              label={editingCoin ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editingCoin
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editingCoin ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editingCoin ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Stat boxes ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <StatBox accentcolor="#C4922A" delay={50}>
              <StatIconBox accentcolor="#C4922A">
                <TollIcon sx={{ fontSize: 20, color: "#C4922A" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {coins.length}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Coin Packs
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#059669" delay={100}>
              <StatIconBox accentcolor="#059669">
                <EmojiEventsIcon sx={{ fontSize: 20, color: "#059669" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalCoins.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Coins
                </Typography>
              </Box>
            </StatBox>

            <StatBox accentcolor="#2563EB" delay={150}>
              <StatIconBox accentcolor="#2563EB">
                <MonetizationOnIcon sx={{ fontSize: 20, color: "#2563EB" }} />
              </StatIconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 400,
                    color: "text.primary",
                    fontFamily: "'DM Serif Display', serif",
                    lineHeight: 1,
                  }}
                >
                  {totalAmount.toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.3,
                  }}
                >
                  Total Value
                </Typography>
              </Box>
            </StatBox>
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={80}>
            <CardLabel>
              {editingCoin ? "Edit Coin Pack" : "Add New Coin Pack"}
            </CardLabel>

            <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              {/* Coins */}
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <FieldLabel>Number of Coins *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <TollIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "#C4922A",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    type="number"
                    placeholder="e.g. 100"
                    value={coinsValue}
                    onChange={(e) => setCoinsValue(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>

              {/* Currency */}
              <Box sx={{ flex: 1, minWidth: 130 }}>
                <FieldLabel>Currency *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <AttachMoneyIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "#C4922A",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    type="text"
                    placeholder="e.g. NGN"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>

              {/* Amount */}
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <FieldLabel>Amount *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <MonetizationOnIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "#C4922A",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    type="number"
                    placeholder="e.g. 500"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editingCoin ? (
                    <CheckIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={loading || !coinsValue || !currency || !amount}
                onClick={editingCoin ? handleUpdateCoin : handleCreateCoin}
              >
                {loading
                  ? "Saving…"
                  : editingCoin
                  ? "Update Coin Pack"
                  : "Add Coin Pack"}
              </PrimaryBtn>
              {editingCoin && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Coins List Card ── */}
          <SectionCard delay={160}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Coin Packs</CardLabel>
              {!fetching && (
                <Chip
                  label={`${coins.length} pack${coins.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#C4922A", 0.09),
                    color: "#C4922A",
                    border: `1px solid ${alpha("#C4922A", 0.22)}`,
                  }}
                />
              )}
            </Box>

            {/* Skeleton */}
            {fetching ? (
              <Box>
                {[...Array(3)].map((_, i) => (
                  <SkeletonCard key={i} sx={{ animationDelay: `${i * 90}ms` }}>
                    <SkeletonBlock w={48} h={48} radius={14} />
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <SkeletonBlock w="30%" h={22} radius={20} />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <SkeletonBlock w={60} h={14} />
                        <SkeletonBlock w={70} h={14} />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <SkeletonBlock w={62} h={30} radius={7} />
                      <SkeletonBlock w={70} h={30} radius={7} />
                    </Box>
                  </SkeletonCard>
                ))}
              </Box>
            ) : coins.length === 0 ? (
              /* Empty state */
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background: alpha("#C4922A", 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <TollIcon
                    sx={{ fontSize: 28, color: "#C4922A", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No coin packs yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to add your first coin pack
                </Typography>
              </Box>
            ) : (
              coins.map((item, i) => (
                <CoinItem
                  key={item.id}
                  item={item}
                  index={i}
                  isEditing={editingCoin?.id === item.id}
                  setCoinsValue={setCoinsValue}
                  setCurrency={setCurrency}
                  setAmount={setAmount}
                  setEditingCoin={setEditingCoin}
                  handleDeleteCoin={handleDeleteCoin}
                />
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminGoldPage;
